import type { V1AutomationInputSpec } from '@/generated/hooks/grimoire/models';
import {
  useAutomationTriggers,
  useTriggerCapabilities,
  type TMatchedTrigger,
} from '@/modules/triggers';
import { areAllRequiredInputsProvided } from '@/modules/automation-inputs/utils/inputValidation';
import { usePermissions } from '@/shared/permissions';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/** What the rail's Triggers block renders. */
export interface IRailTriggers {
  matchedTriggers: TMatchedTrigger[];
  hasSchedule: boolean;
  isScheduleEnabled: boolean;
  scheduleSummary?: string;
  isTriggersLoading: boolean;
  triggerCount: number;
  hasWebhookTrigger: boolean;
  hasPollTrigger: boolean;
  isCapabilitiesLoading: boolean;
  canCreateSchedule: boolean;
  scheduleNeedsDefaultInputs: boolean;
}

/**
 * The Triggers block's data, read by the always-mounted rail.
 *
 * @param automationId - The draft's automation id
 * @param inputSpecs - The automation's inputs
 * @param defaultInputKeys - Inputs that already carry a saved default
 * @returns The Triggers block's data
 */
export function useRailTriggers(
  automationId: string,
  inputSpecs: V1AutomationInputSpec[],
  defaultInputKeys: string[],
): IRailTriggers {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const {
    matchedTriggers,
    hasSchedule,
    isScheduleEnabled,
    scheduleSummary,
    isLoading: isTriggersLoading,
  } = useAutomationTriggers({ automationId, isDraft: true });
  const triggerCount = matchedTriggers.length + (hasSchedule ? 1 : 0);

  // Workspace-scoped and read only by the "+" popover, so its loading state is
  // handed to the picker rather than gating the Triggers block — the same split
  // the published details page makes.
  const {
    hasWebhookTrigger,
    hasPollTrigger,
    isLoading: isCapabilitiesLoading,
  } = useTriggerCapabilities();
  const { hasPermission } = usePermissions();
  const canCreateSchedule = hasPermission(['CreateAutomationSchedule'], {
    organization: organizationId,
    workspace: workspaceId,
  });
  // A schedule runs unattended, so every required input needs a default value.
  const scheduleNeedsDefaultInputs = !areAllRequiredInputsProvided(
    inputSpecs,
    defaultInputKeys,
  );

  return {
    matchedTriggers,
    hasSchedule,
    isScheduleEnabled,
    scheduleSummary,
    isTriggersLoading,
    triggerCount,
    hasWebhookTrigger,
    hasPollTrigger,
    isCapabilitiesLoading,
    canCreateSchedule,
    scheduleNeedsDefaultInputs,
  };
}
